import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Cropper from 'react-cropper'

import { Typography, Theme, Stack, Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge'
import { PreviewFormat } from '@professocial/professocialTypes/types/commonTypes'

import ButtonComponent from './ButtonComponent'
import UploadMediaButton from './UploadMediaButton'
import MediaUploadPreview from './MediaUploadPreview'
import DialogComponent from './DialogComponent'
import PreloaderButton from './PreloaderButton'

export interface RatioSelect {
    label: string
    ratio: number | undefined
}

interface IProps {
    viewMode?: 0 | 1 | 2 | 3
    previewFormat?: PreviewFormat
    mediaFile?: string | undefined
    mediaFileType?: 'image' | 'video' | undefined
    description?: string
    preview?: string | undefined
    hasActualImage?: boolean
    hasPreviewImage?: boolean
    getCropData: () => void
    ratioSelection?:RatioSelect[]
    openRationSelection?: boolean
    allowedExtensions?: string[]
    initialAspectRatio?: number
    aspectRatio?: number
    newImage?: boolean
    loading?: boolean
    deleteImage?: () => void
    onChangeImage?: (imageUrl: string | undefined, file: File | undefined) => void
    onChangeVideo?: (videoUrl: string, file: File | undefined) => void
    onFileUpload?: (fileUrl: string, file: File | undefined) => void
    allowVideoUpload?: boolean
    allowImageUpload?: boolean
    modalCrop?: boolean
    handleCropped: (instance: Cropper) => void
    minCropBoxHeight?: number
    minCropBoxWidth?: number
    containerWidth?: number
    containerHeight?: number
    cropBoxResizable?: boolean
    withButtonPreloader?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  space: {
    margin: '0 0 30px 0',
  },
  ratioSelect: {
    width: '100px',
    background: 'rgba(0, 0, 0, 0.6)',
    color: '#FFF',
    borderRadius: theme.spacing(),
    padding: theme.spacing(),
    position: 'absolute',
    left: '75px',
    top: 0,
    zIndex: 100,
    cursor: 'pointer',
  },
  actionItem: {
    marginLeft: theme.spacing(1),
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}))

const MediaCropperComponent: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const newImage = props?.newImage || false

  const previewFormat = props.previewFormat || PreviewFormat.square

  const [showRatioSelection, setShowRatioSelection] = useState<boolean>(props.openRationSelection || false)
  const [aspectRatio, setAspectRatio] = useState<number | undefined>(props.aspectRatio)
  const [cropper, setCropper] = useState<Cropper>()
  const [imageUploadAllowed] = useState<boolean>(props.allowImageUpload ? props.allowImageUpload : true)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setShowRatioSelection(props.openRationSelection || false)
  }, [props.openRationSelection])

  const getCropperView = () => {
    return (
      <>
        <div className="previewImage"/>
        <Cropper
          style={{ height: props.containerHeight || 400, width: props.containerWidth || '100%' }}
          className={classes.space}
          preview=".previewImage"
          src={props.mediaFile}
          aspectRatio={aspectRatio}
          initialAspectRatio={props.initialAspectRatio ? props.initialAspectRatio : aspectRatio}
          viewMode={props.viewMode || 1}
          guides
          minCropBoxHeight={props.minCropBoxHeight || 10}
          minCropBoxWidth={props.minCropBoxWidth || 10}
          cropBoxResizable={props.cropBoxResizable}
          scalable
          zoomable
          background
          responsive
          autoCrop={true}
          autoCropArea={1}
          checkOrientation={false}
          onInitialized={(instance) => {
            setCropper(instance)
            props.handleCropped(instance)
          }}
        />
      </>
    )
  }

  return (
    <>
      {props.description && <Typography variant="body1" className={classes.space}>{props.description}</Typography>}
      {!props.mediaFile && !props.preview &&
        <Box>
          <UploadMediaButton
            onImageUpload={(imageUrl, file) => {
              props.onChangeImage && props.onChangeImage(imageUrl, file)
              props.modalCrop && setIsOpen(true)
            }}
            onVideoUpload={(videoUrl, file) => {
              props.onChangeVideo && props.onChangeVideo(videoUrl, file)
            }}
            allowVideoUpload={props.allowVideoUpload}
            allowImageUpload={imageUploadAllowed}
            disabled={props.loading}
          />
          {!!props.deleteImage && (props.hasActualImage || props.hasPreviewImage) && (
            <ButtonComponent
              className={classes.actionItem}
              text={t('topicsCommon:delete')}
              color="info"
              variant="outlined"
              disabled={props.loading}
              onClick={props.deleteImage}
            />
          )}
        </Box>
      }
      {props.preview && props.withButtonPreloader &&
        <PreloaderButton
          loading={props.loading}
          disableButton={props.loading}
          position='left'
          color='primary'
          text={t('commonImage:chooseNewFile')}
        />
      }
      {props.preview && !props.withButtonPreloader &&
            <MediaUploadPreview
              view='row'
              previewFormat={previewFormat}
              src={props.preview}
              type={props.mediaFileType}
              deleteMediaFile={() => {
                props.deleteImage && props.deleteImage() // TODO replace with deleteMediaFile function
              }}
            />}
      {props.mediaFile && !props.preview && !props.modalCrop && (
        <>
          {getCropperView()}
          <Stack direction='row' justifyContent={props.ratioSelection ? 'space-between' : 'flex-start'}>
            <Box sx={{ position: 'relative' }}>{(props.ratioSelection && !newImage) &&
                  <>
                    <ButtonComponent
                      size='small'
                      onClick={() => setShowRatioSelection(!showRatioSelection)}
                      text={<PhotoSizeSelectLargeIcon />}
                      disabled={!props.mediaFile}
                    />
                    {showRatioSelection &&
                      <div className={classes.ratioSelect}>
                        {props.ratioSelection.map((elem: RatioSelect, index: number) =>
                          <div
                            key={index}
                            onClick={() => {
                              if (cropper) {
                                // const originRatio = cropper.initialImageData.aspectRatio
                                if (elem.ratio) {
                                  setAspectRatio(elem.ratio)
                                  cropper.setAspectRatio(elem.ratio)
                                } else {
                                  setAspectRatio(undefined)
                                  cropper.setAspectRatio(0)
                                  // cropper.setInitialAspectRatio(originRatio)
                                }
                              }
                              setShowRatioSelection(!showRatioSelection)
                            }}
                          >
                            {elem.label}
                          </div>,
                        )}
                      </div>}
                  </>}
            </Box>
            <Box>
              <ButtonComponent
                color='primary'
                onClick={() => {
                  props.getCropData && props.getCropData()
                }}
                text={!newImage
                  ? t('commonImage:getCroppedImage')
                  : t('commonImage:uploadImageHint')}
              />
            </Box>
          </Stack>
        </>)}
      {props.modalCrop && (
        <DialogComponent
          open={isOpen}
          content={
            <>
              {getCropperView()}
              <ButtonComponent
                color='primary'
                onClick={() => {
                  props.getCropData && props.getCropData()
                  setIsOpen(false)
                }}
                text={!newImage
                  ? t('commonImage:getCroppedImage')
                  : t('commonImage:uploadImageHint')}
              />
            </>
          }
          onClose={() => {
            props.onChangeImage && props.onChangeImage(undefined, undefined)
            setIsOpen(false)
          }}
          autoClose={false}
          closeIcon
        />
      )}
    </>
  )
}

export default MediaCropperComponent
